import type { Localization } from "@mui/material/locale";

export type MuiAppLang = "en" | "zh" | "jp";

export function normalizeMuiLanguage(language: string): MuiAppLang {
  if (language === "zh" || language === "jp") return language;
  return "en";
}

/** Resolved locale bundles passed to `createTheme`. */
export type MuiThemeLocales = {
  core: Localization;
  grid: object;
  pickers: object;
};

/**
 * Loads Material / Data Grid / Pickers locales for one language (async chunks).
 * Note: `@mui/material/locale` is a single large module; dynamic import moves it
 * out of main.js. Grid and pickers use per-locale files when imported by path.
 */
export async function loadMuiThemeLocales(
  lang: MuiAppLang,
): Promise<MuiThemeLocales> {
  const materialMod = await import("@mui/material/locale");
  
  switch (lang) {
    case "zh": {
      const [gridMod, pickersMod] = await Promise.all([
        import("@mui/x-data-grid/locales/zhCN"),
        import("@mui/x-date-pickers/locales/zhCN"),
      ]);
      return {
        core: materialMod.zhCN,
        grid: gridMod.zhCN,
        pickers: pickersMod.zhCN,
      };
    }
    case "jp": {
      const [gridMod, pickersMod] = await Promise.all([
        import("@mui/x-data-grid/locales/jaJP"),
        import("@mui/x-date-pickers/locales/jaJP"),
      ]);
      return {
        core: materialMod.jaJP,
        grid: gridMod.jaJP,
        pickers: pickersMod.jaJP,
      };
    }
    default: {
      const [gridMod, pickersMod] = await Promise.all([
        import("@mui/x-data-grid/locales/enUS"),
        import("@mui/x-date-pickers/locales/enUS"),
      ]);
      return {
        core: materialMod.enUS,
        grid: gridMod.enUS,
        pickers: pickersMod.enUS,
      };
    }
  }
}

/** Grid locale theme object (used for localeText extraction); one file per language. */
export async function loadDataGridLocaleTheme(lang: MuiAppLang) {
  switch (lang) {
    case "zh":
      return (await import("@mui/x-data-grid/locales/zhCN")).zhCN;
    case "jp":
      return (await import("@mui/x-data-grid/locales/jaJP")).jaJP;
    default:
      return (await import("@mui/x-data-grid/locales/enUS")).enUS;
  }
}
